import { CboRole, FeatureFlag, Package } from '@cbo/shared-library';
import routes from '../../../constants/routes';

export type ReportAccessEntry = {
  id: number;
  permissions: CboRole[];
  reportName: string;
  section: string;
  description: string;
  category: string;
  featureFlag?: FeatureFlag;
  reportId?: string;
  packagesRequired?: Package[];
};

export const reportAccessMap: Record<string, ReportAccessEntry> = {
  // Labor Reports
  [routes.EMPLOYEE_ACA_THRESHOLD_REPORT]: {
    id: 0,
    permissions: [CboRole.ACA_THRESHOLD_VIEW],
    reportName: 'navItems.employeeACAReport',
    section: 'admin.centralReportsPage.sectionHeaders.laborReports',
    description: 'admin.centralReportsPage.descriptions.approachingAcaThreshold',
    category: 'navItems.laborManagement',
  },
  [routes.APPROACHING_OVERTIME_THRESHOLD]: {
    id: 1,
    permissions: [CboRole.APPROACHING_OVERTIME_REPORT_VIEW],
    reportName: 'navItems.approachingOvertimeThreshold',
    section: 'admin.centralReportsPage.sectionHeaders.laborReports',
    description: 'admin.centralReportsPage.descriptions.approachingOtThreshold',
    category: 'navItems.laborManagement',
  },
  [routes.EMPLOYEE_BREAK]: {
    id: 2,
    permissions: [CboRole.EMPLOYEE_BREAK_VIEW],
    reportName: 'navItems.employeeBreak',
    section: 'admin.centralReportsPage.sectionHeaders.laborReports',
    description: 'admin.centralReportsPage.descriptions.employeeBreak',
    category: 'navItems.laborManagement',
  },
  [routes.EMPLOYEE_PAYROLL_SUMMARY_REPORT]: {
    id: 3,
    reportId: 'employee_payroll_report',
    permissions: [CboRole.PAYROLL_VIEW, CboRole.EMPLOYEE_VIEW],
    reportName: 'navItems.employeePayrollSummaryReport',
    section: 'admin.centralReportsPage.sectionHeaders.laborReports',
    description: 'admin.centralReportsPage.descriptions.employeePayroll',
    category: 'navItems.laborManagement',
  },
  [routes.EMPLOYEE_SALES_PERFORMANCE]: {
    id: 4,
    permissions: [CboRole.EMPLOYEE_PERFORMANCE_VIEW],
    reportName: 'navItems.employeeSalesPerformance',
    section: 'admin.centralReportsPage.sectionHeaders.laborReports',
    description: 'admin.centralReportsPage.descriptions.employeeSalesPerformance',
    category: 'navItems.laborManagement',
  },
  [routes.GENERIC_PAYROLL_EXPORT]: {
    id: 5,
    reportId: 'generic_payroll_export',
    permissions: [CboRole.PAYROLL_EXPORT_VIEW],
    reportName: 'navItems.genericPayrollExport',
    section: 'admin.centralReportsPage.sectionHeaders.laborReports',
    description: 'admin.centralReportsPage.descriptions.genericPayrollExport',
    category: 'navItems.laborManagement',
  },
  [routes.EMPLOYEE_TIP_REPORT]: {
    id: 6,
    reportId: 'employee_tip_report',
    permissions: [CboRole.EMPLOYEE_TIPS_VIEW],
    featureFlag: FeatureFlag.laborTipReports,
    reportName: 'navItems.employeeTip',
    section: 'admin.centralReportsPage.sectionHeaders.laborReports',
    description: 'admin.centralReportsPage.descriptions.employeeTip',
    category: 'navItems.laborManagement',
  },
  [routes.INTERVAL_SALES_AND_LABOR]: {
    id: 7,
    permissions: [CboRole.INTERVAL_SALES_LABOR_VIEW],
    reportName: 'navItems.intervalSalesAndLaborReport',
    section: 'admin.centralReportsPage.sectionHeaders.laborReports',
    description: 'admin.centralReportsPage.descriptions.intervalSalesAndLabor',
    category: 'navItems.laborManagement',
  },

  // Inventory Reports
  [routes.COGS_REPORT]: {
    id: 8,
    permissions: [CboRole.COGS_REPORT_VIEW],
    reportName: 'navItems.cogs',
    section: 'admin.centralReportsPage.sectionHeaders.inventoryReports',
    description: 'admin.centralReportsPage.descriptions.cogs',
    category: 'navItems.inventoryManagement',
    packagesRequired: [Package.INVENTORY],
  },
  [routes.PERPETUAL_INVENTORY_ON_HAND]: {
    id: 9,
    permissions: [CboRole.PERPETUAL_INVENTORY_REPORT_VIEW],
    featureFlag: FeatureFlag.perpetualInventoryReport,
    reportName: 'navItems.perpetualInventoryOnHand',
    section: 'admin.centralReportsPage.sectionHeaders.inventoryReports',
    description: 'admin.centralReportsPage.descriptions.perpetualInventoryOnHand',
    category: 'navItems.inventoryManagement',
    packagesRequired: [Package.INVENTORY],
  },
  [routes.LOW_STOCK_REPORT]: {
    id: 25,
    permissions: [CboRole.LOW_STOCK_REPORT_VIEW],
    featureFlag: FeatureFlag.lowStockReport,
    reportName: 'navItems.lowStockReport',
    section: 'admin.centralReportsPage.sectionHeaders.inventoryReports',
    description: 'admin.centralReportsPage.descriptions.lowStockReport',
    category: 'navItems.inventoryManagement',
    packagesRequired: [Package.INVENTORY],
  },
  [routes.TOP_20_ITEM_USAGE_REPORT]: {
    id: 26,
    permissions: [CboRole.CBO_REPORT_TOP20_BOTTOM10_VIEWER],
    featureFlag: FeatureFlag.top20ItemsReport,
    reportName: 'navItems.top20ItemUsageReport',
    section: 'admin.centralReportsPage.sectionHeaders.inventoryReports',
    description: 'admin.centralReportsPage.descriptions.top20ItemUsageReport',
    category: 'navItems.inventoryManagement',
    packagesRequired: [Package.INVENTORY],
  },
  [routes.REAL_TIME_INVENTORY]: {
    id: 27,
    permissions: [CboRole.CBO_REPORT_TOP20_BOTTOM10_VIEWER], // To-Do need to replace with realtime inventory role
    featureFlag: FeatureFlag.realTimeInventory,
    reportName: 'navItems.realTimeInventory',
    section: 'admin.centralReportsPage.sectionHeaders.inventoryReports',
    description: 'admin.centralReportsPage.descriptions.realTimeInventory',
    category: 'navItems.inventoryManagement',
    packagesRequired: [Package.INVENTORY],
  },
  [routes.INVOICE_HISTORY_REPORT]: {
    id: 10,
    permissions: [CboRole.INVOICE_VIEW],
    reportName: 'inventory.invoiceHistoryReport.invoiceHistory',
    section: 'admin.centralReportsPage.sectionHeaders.inventoryReports',
    description: 'admin.centralReportsPage.descriptions.invoiceHistory',
    category: 'navItems.inventoryManagement',
  },

  // Sales Reports
  [routes.SALES_COMPARATIVE_SALES_LABOR_REPORT]: {
    id: 23,
    permissions: [CboRole.COMPARATIVE_SALES_LABOR_REPORT_VIEW],
    featureFlag: FeatureFlag.comparativeSalesLaborReport,
    reportName: 'navItems.cslReport',
    section: 'admin.centralReportsPage.sectionHeaders.salesReports',
    description: 'admin.centralReportsPage.descriptions.comparativeSalesLabor',
    category: 'navItems.salesManagement',
  },
  [routes.SALES_DISCOUNT_REPORT]: {
    id: 11,
    permissions: [CboRole.DISCOUNT_REPORT_VIEW],
    reportName: 'navItems.discountReport',
    section: 'admin.centralReportsPage.sectionHeaders.salesReports',
    description: 'admin.centralReportsPage.descriptions.discounts',
    category: 'navItems.salesManagement',
  },
  [routes.FINANCIAL_SHIFT_MANAGER]: {
    id: 12,
    permissions: [CboRole.FINANCIAL_SHIFT_MANAGER_VIEWER], // Not fully sure if this role is done right (might be two roles)
    featureFlag: FeatureFlag.cashOffice,
    reportName: 'navItems.financialShiftManager',
    section: 'admin.centralReportsPage.sectionHeaders.salesReports',
    description: 'admin.centralReportsPage.descriptions.financialShiftManager',
    category: 'navItems.salesManagement',
  },
  [routes.SALES_JOURNAL_ENTRY]: {
    id: 24,
    permissions: [CboRole.GL_ACCOUNTS_READ],
    featureFlag: FeatureFlag.journalEntry,
    reportName: 'navItems.journalEntries',
    section: 'admin.centralReportsPage.sectionHeaders.salesReports',
    description: 'admin.centralReportsPage.descriptions.journalEntries',
    category: 'navItems.salesManagement',
  },
  [routes.SALES_PAYMENT_REPORT]: {
    id: 13,
    reportId: 'payments_report',
    permissions: [CboRole.PAYMENT_REPORT_VIEW],
    reportName: 'navItems.paymentReport',
    section: 'admin.centralReportsPage.sectionHeaders.salesReports',
    description: 'admin.centralReportsPage.descriptions.payments',
    category: 'navItems.salesManagement',
  },
  [routes.SALES_POS_EVENT_LOG]: {
    id: 14,
    permissions: [CboRole.POS_EVENT_LOG_VIEW],
    reportName: 'navItems.posEventLog',
    section: 'admin.centralReportsPage.sectionHeaders.salesReports',
    description: 'admin.centralReportsPage.descriptions.posEventLog',
    category: 'navItems.salesManagement',
  },
  [routes.SALES_PRODUCT_MIX_REPORT]: {
    id: 15,
    reportId: 'product_mix_report',
    permissions: [CboRole.PRODUCT_MIX_REPORT_VIEW],
    featureFlag: FeatureFlag.hideAlohaEssentialOrgs,
    reportName: 'navItems.productMixReport',
    section: 'admin.centralReportsPage.sectionHeaders.salesReports',
    description: 'admin.centralReportsPage.descriptions.productMix',
    category: 'navItems.salesManagement',
  },
  [routes.SALES_PROFIT_LOSS_REPORT]: {
    id: 16,
    permissions: [CboRole.PROFIT_LOSS_REPORT_VIEW],
    featureFlag: FeatureFlag.profitLossReport,
    reportName: 'navItems.profitLossReport',
    section: 'admin.centralReportsPage.sectionHeaders.salesReports',
    description: 'admin.centralReportsPage.descriptions.profitLoss',
    category: 'navItems.salesManagement',
  },
  [routes.SALES_REFUNDS_REPORT]: {
    id: 17,
    permissions: [CboRole.REFUND_REPORT_VIEW],
    reportName: 'navItems.refundsReport',
    section: 'admin.centralReportsPage.sectionHeaders.salesReports',
    description: 'admin.centralReportsPage.descriptions.refunds',
    category: 'navItems.salesManagement',
  },
  [routes.SALES_REVENUE_CENTER_REPORT]: {
    id: 18,
    permissions: [CboRole.REVENUE_CENTER_REPORT_VIEW],
    reportName: 'navItems.revenueCenterReport',
    section: 'admin.centralReportsPage.sectionHeaders.salesReports',
    description: 'admin.centralReportsPage.descriptions.revenueCenters',
    category: 'navItems.salesManagement',
  },
  [routes.SALES_SUMMARY_REPORT]: {
    id: 19,
    reportId: 'sales_summary',
    permissions: [CboRole.SALES_SUMMARY_VIEW],
    reportName: 'navItems.salesSummaryReport',
    section: 'admin.centralReportsPage.sectionHeaders.salesReports',
    description: 'admin.centralReportsPage.descriptions.salesSummary',
    category: 'navItems.salesManagement',
  },
  [routes.SALES_TAX_REPORT]: {
    id: 20,
    permissions: [CboRole.TAX_REPORT_VIEW],
    reportName: 'navItems.taxesReport',
    section: 'admin.centralReportsPage.sectionHeaders.salesReports',
    description: 'admin.centralReportsPage.descriptions.taxes',
    category: 'navItems.salesManagement',
  },
  [routes.SALES_TRANSACTION_DETAILS_REPORT]: {
    id: 21,
    reportId: 'transaction_summary',
    permissions: [CboRole.TRANSACTION_DETAIL_VIEW],
    featureFlag: FeatureFlag.hideAlohaEssentialOrgs,
    reportName: 'navItems.transactionDetailsReport',
    section: 'admin.centralReportsPage.sectionHeaders.salesReports',
    description: 'admin.centralReportsPage.descriptions.transactions',
    category: 'navItems.salesManagement',
  },
  [routes.SALES_VOID_REPORT]: {
    id: 22,
    permissions: [CboRole.VOID_REPORT_VIEW],
    reportName: 'navItems.voidReport',
    section: 'admin.centralReportsPage.sectionHeaders.salesReports',
    description: 'admin.centralReportsPage.descriptions.voids',
    category: 'navItems.salesManagement',
  },
};
